import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
  AGENT_SESSION_HINT_KEY,
  clearSessionHint,
  fetchSessionMe,
  hasSessionHint,
  setSessionHint,
} from '../utils/authSession';
import { getAgentPaymentNavState } from '../utils/agentPaymentNav';

const AgentAuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

export const AgentAuthProvider = ({ children }) => {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (!hasSessionHint(AGENT_SESSION_HINT_KEY)) {
      setLoading(false);
      return;
    }
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetchSessionMe(`${API}/agent/me`);
      if (response.status === 401) {
        clearSessionHint(AGENT_SESSION_HINT_KEY);
        setAgent(null);
        setIsAuthenticated(false);
        return;
      }
      setAgent(response.data);
      setIsAuthenticated(true);
      setSessionHint(AGENT_SESSION_HINT_KEY);
    } catch (error) {
      setAgent(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = (agentData) => {
    setSessionHint(AGENT_SESSION_HINT_KEY);
    setAgent(agentData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/agent/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearSessionHint(AGENT_SESSION_HINT_KEY);
      setAgent(null);
      setIsAuthenticated(false);
    }
  };

  const updateAgent = (updatedAgentData) => {
    const newAgent = { ...agent, ...updatedAgentData };
    setAgent(newAgent);
  };

  const refreshWallet = async () => {
    if (!isAuthenticated) return;

    try {
      const response = await axios.get(`${API}/agent/wallet/balance`);
      updateAgent({ wallet_balance: response.data.wallet_balance });
      return response.data.wallet_balance;
    } catch (error) {
      console.error('Failed to refresh wallet:', error);
      return agent?.wallet_balance || 0;
    }
  };

  const refreshCreditBalance = async () => {
    if (!isAuthenticated) return null;

    try {
      const response = await axios.get(`${API}/agent/credit/balance`);

      const d = response.data;
      const creditOn = d.credit_enabled
        || d.agent_payment_capabilities?.credit_enabled
        || ['CREDIT', 'HYBRID'].includes(d.payment_mode);
      if (creditOn) {
        updateAgent({
          payment_mode: d.payment_mode,
          agent_payment_capabilities: d.agent_payment_capabilities,
          wallet_balance: d.wallet_balance ?? agent?.wallet_balance,
          credit_limit: d.credit_limit,
          credit_used: d.credit_used,
          credit_available: d.credit_available,
          credit_status: d.credit_status,
          overdue_amount: d.overdue_amount,
        });
      } else {
        updateAgent({
          payment_mode: d.payment_mode || 'CASH',
          agent_payment_capabilities: d.agent_payment_capabilities,
          wallet_balance: d.wallet_balance ?? agent?.wallet_balance,
        });
      }

      return response.data;
    } catch (error) {
      console.error('Failed to refresh credit balance:', error);
      return null;
    }
  };

  const getPaymentEligibility = async () => {
    if (!isAuthenticated) return null;

    try {
      const response = await axios.get(`${API}/agent/booking/payment-eligibility`);
      return response.data;
    } catch (error) {
      console.error('Failed to get payment eligibility:', error);
      return null;
    }
  };

  const refreshAgentBalances = async () => {
    if (!isAuthenticated || !agent) return;

    const { isCreditAgent, walletEnabled } = getAgentPaymentNavState(agent);
    await Promise.all([
      walletEnabled ? refreshWallet() : null,
      isCreditAgent ? refreshCreditBalance() : null,
    ].filter(Boolean));
  };

  const value = {
    agent,
    loading,
    isAuthenticated,
    login,
    logout,
    updateAgent,
    refreshWallet,
    refreshCreditBalance,
    refreshAgentBalances,
    getPaymentEligibility,
    checkAuthStatus,
  };

  return (
    <AgentAuthContext.Provider value={value}>
      {children}
    </AgentAuthContext.Provider>
  );
};

export const useAgentAuth = () => {
  const context = useContext(AgentAuthContext);
  if (!context) {
    throw new Error('useAgentAuth must be used within AgentAuthProvider');
  }
  return context;
};

export default AgentAuthContext;
